import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { VersionService } from '../services/version.service';
import { MenuController, Platform, IonicModule } from '@ionic/angular';
import { LocalAuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';
import { PermissionService } from '../services/permission.service';
import { FEATURE } from '../constants/feature-keys';
import { LegalLinksService } from '../constants/legal-links';
import { LanguageService } from '../services/language.service';
import { SupportedLanguage } from '../i18n/translations';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '../pipes/translate.pipe';

@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        IonicModule,
        RouterLinkActive,
        RouterLink,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class SideMenuComponent implements OnInit {
  private readonly authService = inject(LocalAuthService);
  private readonly tenantService = inject(TenantService);
  private readonly permissions = inject(PermissionService);
  private readonly menuController = inject(MenuController);
  private readonly languageService = inject(LanguageService);
  private readonly platform = inject(Platform);
  private readonly versions = inject(VersionService);
  /** Public: the footer links to the portal-hosted legal texts straight from the template. */
  readonly legal = inject(LegalLinksService);

  readonly isAuthenticated$ = this.authService.isAuthenticated$;
  readonly user$ = this.authService.user$;
  readonly hasTenant$ = this.tenantService.hasTenant$;
  /** Empty list *because the load failed* — a different message from "no company assigned". */
  readonly tenantsUnavailable$ = this.tenantService.tenantsUnavailable$;
  readonly activeTenant$ = this.tenantService.activeTenant$;
  readonly activeTenantId$ = this.tenantService.activeTenantId$;
  readonly currentLanguage$ = this.languageService.currentLanguage$;
  readonly canIntegrations$ = this.permissions.can$(FEATURE.integrations, 'read');
  readonly canAlerts$ = this.permissions.can$(FEATURE.alerts, 'read');
  readonly canSettings$ = this.permissions.can$(FEATURE.tenantSettings, 'write');
  readonly canUsers$ = this.permissions.isAdmin$();
  readonly canAuditLog$ = this.permissions.can$(FEATURE.auditLog, 'read');
  readonly canSupport$ = this.permissions.can$(FEATURE.support, 'read');
  readonly languageOptions: SupportedLanguage[] = ['de', 'en'];
  readonly isMobile = this.platform.is('mobile') || this.platform.is('mobileweb');
  readonly spaVersion = this.versions.spaVersion;
  readonly apiVersion$ = this.versions.apiVersion$;

  ngOnInit(): void {
    try {
      const saved = localStorage.getItem('sd_menu_groups');
      if (saved) this.groupCollapsed = JSON.parse(saved);
    } catch {}
  }

  logout() {
    this.authService.logout();
  }

  retryTenants(): void {
    this.tenantService.retryLoad();
  }

  async closeMenu(): Promise<void> {
    await this.menuController.close();
  }

  onLanguageChange(value: string | null | undefined): void {
    const language = this.languageService.normalizeLanguage(value);
    if (!language) {
      return;
    }

    this.languageService.setLanguage(language).subscribe();
  }

  getLanguageOptionLabel(language: SupportedLanguage): string {
    return `${this.getLanguageFlag(language)} ${this.languageService.translate(`lang.${language}`)}`;
  }

  private getLanguageFlag(language: SupportedLanguage): string {
    return language === 'de' ? '🇩🇪' : '🇬🇧';
  }

  groupCollapsed: Record<string, boolean> = { media: true, settings: true };

  toggleGroup(group: string): void {
    this.groupCollapsed[group] = !this.groupCollapsed[group];
    try { localStorage.setItem('sd_menu_groups', JSON.stringify(this.groupCollapsed)); } catch {}
  }

  isGroupCollapsed(group: string): boolean {
    return !!this.groupCollapsed[group];
  }

  showLangPicker = false;
  langPickerEvent?: Event;

  openLangPicker(event: Event): void {
    this.langPickerEvent = event;
    this.showLangPicker = true;
  }

  closeLangPicker(): void {
    this.showLangPicker = false;
  }

  selectLanguage(lang: SupportedLanguage): void {
    this.onLanguageChange(lang);
    this.showLangPicker = false;
  }

  getFlagPath(lang: SupportedLanguage): string {
    return `assets/flags/${lang}.svg`;
  }

  getLanguageName(lang: SupportedLanguage): string {
    return this.languageService.translate(`lang.${lang}`);
  }

  trackByLanguage(_index: number, lang: SupportedLanguage): SupportedLanguage {
    return lang;
  }

  handleTenantLogoError(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (!image) {
      return;
    }

    image.src = 'assets/title.svg';
  }

}
